// src/hooks/usePermisos.js
// ─────────────────────────────────────────────────────────────────────────────
// Uso en cualquier módulo:
//
//   import { usePermisos } from '../hooks/usePermisos';
//
//   const { tieneModulo, tieneOpcion, cargando } = usePermisos();
//
//   // Verificar acceso al módulo raíz:
//   if (!tieneModulo('farmacia')) return <SinAcceso />;
//
//   // Verificar acceso a una opción (moduloId = PERM_IDMODULO del SP):
//   {tieneOpcion('ventas', 7) && <Button>Nueva Venta</Button>}
//   {tieneOpcion('kardex', 7) && <Button>Ver Kardex</Button>}
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

// Cache en memoria para no hacer múltiples llamadas al backend
let _cache = null;
let _promise = null;
let _generation = 0;

const fetchPermisos = () => {
  if (_cache) return Promise.resolve(_cache);
  if (_promise) return _promise;
  const generation = _generation;
  _promise = api.get('/dashboard/permisos')
    .then(r => {
      if(generation !== _generation) return {modulos:[],opciones:[]};
      if (r.data.success) {
        _cache = { modulos: r.data.modulos || [], opciones: r.data.opciones || [] };
      } else {
        _cache = { modulos: [], opciones: [] };
      }
      return _cache;
    })
    .catch(() => {
      if(generation !== _generation) return {modulos:[],opciones:[]};
      _cache = { modulos: [], opciones: [] };
      return _cache;
    })
    .finally(() => { if(generation === _generation) _promise = null; });
  return _promise;
};

// Llamar esto al hacer logout para limpiar el cache
export const limpiarCachePermisos = () => { _generation++; _cache = null; _promise = null; };
window.addEventListener("integra:logout", limpiarCachePermisos);

export const usePermisos = () => {
  const [permisos, setPermisos] = useState(_cache); // si ya estaba en cache, carga instantáneo
  const [cargando, setCargando] = useState(!_cache);

  useEffect(() => {
    if (_cache) { setPermisos(_cache); setCargando(false); return; }
    setCargando(true);
    fetchPermisos().then(p => { setPermisos(p); setCargando(false); });
  }, []);

  /** ¿El usuario tiene acceso al módulo raíz? ej: tieneModulo('farmacia') */
  const tieneModulo = useCallback((id) => {
    if (!permisos) return false;
    return permisos.modulos.includes(id);
  }, [permisos]);

  /** ¿El usuario tiene una opción dentro de un módulo?
   *  ej: tieneOpcion('ventas', 7)  — "ventas" es PERM_PERMISO_DESC, 7 es PERM_IDMODULO
   */
  const tieneOpcion = useCallback((permiso, moduloId) => {
    if (!permisos) return false;
    return permisos.opciones.some(o => o.permiso === permiso && o.moduloId === moduloId);
  }, [permisos]);

  /** Lista de opciones disponibles para un módulo dado */
  const opcionesDeModulo = useCallback((moduloId) => {
    if (!permisos) return [];
    return permisos.opciones.filter(o => o.moduloId === moduloId).map(o => o.permiso);
  }, [permisos]);

  /** Retorna el privilegio de una opción específica
   *  ej: privilegioDeOpcion('devoluciones', 7) → 'Total' | 'Lectura/Escritura' | 'Solo lectura'
   */
  const privilegioDeOpcion = useCallback((permiso, moduloId) => {
    if (!permisos) return 'Solo lectura';
    const op = permisos.opciones.find(o => o.permiso === permiso && o.moduloId === moduloId);
    return op?.privilegio || 'Solo lectura';
  }, [permisos]);

  return { permisos, cargando, tieneModulo, tieneOpcion, opcionesDeModulo, privilegioDeOpcion };
};
